'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { CheckCircle2, ClipboardList, Eye } from 'lucide-react'
import {
  CASE_STATUS_LABEL,
  CaseStatusBadge,
  FlowBadge,
  SERVICE_TYPE_LABEL,
  SERVICE_TYPE_TO_FLOW,
  StatCard,
  useAssignAdvisor,
  useCases,
  useUnassignAdvisor,
  type CaseStatus,
  type CaseSummary,
} from '@/features/cases'
import { useAdminUsers } from '@/features/admin'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { formatDate } from '@/lib/format/date'
import { ALL_FILTER_VALUE, TableSearch } from '@/components/patterns/table-filter-tabs'
import { TableFilterSelect } from '@/components/patterns/table-filter-select'
import { Pagination } from '@/components/patterns/pagination'
import { QueryErrorState } from '@/components/patterns/queryErrorState'
import { ListSkeleton } from '@/components/patterns/listSkeleton'
import { EmptyState } from '@/components/patterns/emptyState'
import { ADVISOR_UNASSIGNED_VALUE, AdvisorCombobox } from '@/components/patterns/advisor-combobox'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AdminShell } from '@/features/admin/components/admin-shell'

const PAGE_SIZE = 20

type StatusFilter = typeof ALL_FILTER_VALUE | CaseStatus

export function AdminCasesView() {
  const router = useRouter()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(ALL_FILTER_VALUE)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebouncedValue(search, 300)

  const { data, isLoading, isError, error, refetch } = useCases({
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
    status: statusFilter === ALL_FILTER_VALUE ? undefined : statusFilter,
    sort: 'newest',
  })
  const cases = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const counts = data?.counts
  const statusOptions = useMemo(
    () =>
      (counts ?? [])
        .filter((c) => c.count > 0)
        .map((c) => ({ value: c.status, label: CASE_STATUS_LABEL[c.status], count: c.count })),
    [counts],
  )
  // The "All" tab's own count must also come from the all-status `counts` array, not the
  // paginated `total` — `total` is scoped to whichever status tab is active, so it would
  // otherwise show e.g. "All (120)" while a hidden 38 consultation_scheduled cases exist too.
  const grandTotal = useMemo(() => (counts ?? []).reduce((sum, c) => sum + c.count, 0), [counts])

  const submittedCount = counts?.find((c) => c.status === 'submitted')?.count ?? 0
  const publishedCount = counts?.find((c) => c.status === 'published')?.count ?? 0
  // Derived from `counts` (always all-status, scoped only by search/serviceType/seriousness —
  // see api-progress.md's Phase C entry), never from the paginated `total`, which is scoped to
  // whichever status tab is active and would otherwise make this go negative the moment a status
  // other than "All" is selected.
  const inProgressCount = (counts ?? [])
    .filter((c) => c.status !== 'submitted' && c.status !== 'published')
    .reduce((sum, c) => sum + c.count, 0)

  const openCase = (c: CaseSummary) => router.push(`/admin/clients/${c.investorId}?case=${c.id}`)

  // `pageSize: 100` (the endpoint's max) rather than the default page — this dropdown needs the
  // whole roster to assign from, not one page of it; `/admin/staff` is the only screen that
  // actually paginates this list.
  const { data: staffPage } = useAdminUsers({ pageSize: 100 })
  const staff = useMemo(() => staffPage?.items ?? [], [staffPage])
  const activeStaff = useMemo(() => staff.filter((s) => s.role === 'staff' && s.isActive), [staff])
  const assignAdvisor = useAssignAdvisor()
  const unassignAdvisor = useUnassignAdvisor()
  const [pendingAssign, setPendingAssign] = useState<{
    case: CaseSummary
    staffId: string
    staffName: string
  } | null>(null)

  // Staged, not immediate — a misclick on this dropdown would otherwise instantly reassign a
  // case with no way to undo. The combobox's displayed value stays bound to `c.advisorId`, so
  // cancelling the dialog naturally reverts it without any extra state to manage.
  const onAdvisorChange = (c: CaseSummary, value: string) => {
    if (value === ADVISOR_UNASSIGNED_VALUE) {
      unassignAdvisor.mutate(c.id)
      return
    }
    const staffName = activeStaff.find((s) => s.id === value)?.name ?? 'this staff member'
    setPendingAssign({ case: c, staffId: value, staffName })
  }

  const confirmAssign = () => {
    if (!pendingAssign) return
    assignAdvisor.mutate({ caseId: pendingAssign.case.id, staffId: pendingAssign.staffId })
    setPendingAssign(null)
  }

  return (
    <AdminShell
      active="cases"
      title="Cases"
      description="Every audit, plan, and trade review moving through the pipeline."
    >
      <div className="@xl/main:grid-cols-3 grid grid-cols-1 gap-4">
        <StatCard
          label="Submitted"
          value={submittedCount}
          footnote="Waiting on first review"
          icon={<ClipboardList />}
          tint="var(--chart-5)"
        />
        <StatCard
          label="In Progress"
          value={inProgressCount}
          footnote="Being worked by an advisor"
          icon={<Eye />}
          tint="var(--chart-3)"
        />
        <StatCard
          label="Published"
          value={publishedCount}
          trend="Complete"
          trendUp
          footnote="Delivered to the investor"
          icon={<CheckCircle2 />}
          tint="var(--chart-2)"
        />
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="@lg/main:flex-row @lg/main:items-center @lg/main:justify-between flex flex-col gap-3">
            <TableFilterSelect
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v)
                setPage(1)
              }}
              allLabel="All"
              allCount={grandTotal}
              options={statusOptions}
            />
            <TableSearch
              value={search}
              onChange={(v) => {
                setSearch(v)
                setPage(1)
              }}
              placeholder="Search name or reference…"
              count={{ shown: cases.length, total, noun: 'cases' }}
            />
          </div>

          {isError ? (
            <QueryErrorState error={error} onRetry={() => void refetch()} />
          ) : isLoading ? (
            <ListSkeleton rows={6} />
          ) : cases.length === 0 ? (
            <EmptyState title="No cases match this filter." />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Investor</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Advisor</TableHead>
                      <TableHead className="text-right">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cases.map((c) => (
                      <TableRow key={c.id} className="cursor-pointer" onClick={() => openCase(c)}>
                        <TableCell className="font-medium">{c.investorName ?? '—'}</TableCell>
                        <TableCell className="text-muted-foreground font-mono text-xs">
                          {c.reference}
                        </TableCell>
                        <TableCell>
                          <FlowBadge flow={SERVICE_TYPE_TO_FLOW[c.serviceType]} />
                        </TableCell>
                        <TableCell>
                          <div className="flex min-w-0">
                            <CaseStatusBadge status={c.caseStatus} />
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <AdvisorCombobox
                            value={c.advisorId ?? ADVISOR_UNASSIGNED_VALUE}
                            onValueChange={(v) => onAdvisorChange(c, v)}
                            options={activeStaff}
                          />
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right tabular-nums">
                          {formatDate(c.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!pendingAssign} onOpenChange={(open) => !open && setPendingAssign(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign this case to {pendingAssign?.staffName}?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAssign?.case.investorName ?? 'This investor'}&apos;s{' '}
              {pendingAssign ? SERVICE_TYPE_LABEL[pendingAssign.case.serviceType] : ''} case will be
              assigned to {pendingAssign?.staffName}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAssign}>Assign</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  )
}
